import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Alert } from 'assets/icons/Alert.svg';
import { ReactComponent as X } from 'assets/icons/X.svg';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import testingConstants from 'utils/testingConstants';

import { API_KEYS_MODAL_WRAPPER_ID } from './constants';

export type ApiKeyErrorOperation = 'list' | 'create' | 'rotate' | 'revoke';

const TITLE_KEYS: Record<ApiKeyErrorOperation, string> = {
  list: 'workspaces_settings.api_keys.errors.list_title',
  create: 'workspaces_settings.api_keys.errors.create_title',
  rotate: 'workspaces_settings.api_keys.errors.rotate_title',
  revoke: 'workspaces_settings.api_keys.errors.revoke_title',
};

const BODY_KEYS: Record<ApiKeyErrorOperation, string> = {
  list: 'workspaces_settings.api_keys.errors.list_body',
  create: 'workspaces_settings.api_keys.errors.create_body',
  rotate: 'workspaces_settings.api_keys.errors.rotate_body',
  revoke: 'workspaces_settings.api_keys.errors.revoke_body',
};

const GENERIC_BODY_KEY = 'workspaces_settings.api_keys.errors.generic_body';

interface ApiKeyActionErrorModalProps {
  /** Which request failed — selects the title/body copy (error-handling.md). */
  operation: ApiKeyErrorOperation;
  /** Re-executes the failed request. Caller keeps the create/rotate form mounted so state is preserved (AC-04-09). */
  onRetry: () => void;
  /** Optional callback on dismiss — e.g. refresh the list after a KEY_NOT_FOUND. */
  onClose?: () => void;
  /** Network/timeout failures have no operation-specific cause → show the generic body (error-handling.md §Network errors). */
  useGenericBody?: boolean;
}

/**
 * Shared error modal for every API-key request failure (Figma 6469:166668). Centered
 * alert icon + operation-specific copy + a single "Try again" action that re-runs the
 * failed request via `onRetry`.
 */
const ApiKeyActionErrorModal = create<ApiKeyActionErrorModalProps>(
  ({ operation, onRetry, onClose, useGenericBody = false }) => {
    const { t } = useTranslation();
    const { remove } = useModal(ApiKeyActionErrorModal);

    const handleClose = () => {
      onClose?.();
      remove();
    };

    const handleRetry = () => {
      remove();
      onRetry();
    };

    return (
      <Popup isVisible closePopup={handleClose} isViewportScrollable wrapperId={API_KEYS_MODAL_WRAPPER_ID}>
        <Popup.Container
          className="relative md:w-[512px] md:max-w-[512px]"
          dataTestId={testingConstants.apiKeyActionErrorModal}
        >
          <div className="flex flex-col px-4 pb-12 pt-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                aria-label={t('close')}
                className="hover:bg-gray-30 flex h-8 w-8 items-center justify-center rounded-xl p-2.5"
              >
                <X className="stroke-grayscale-black h-5 w-5 stroke-2" />
              </button>
            </div>

            <div className="flex w-full flex-col items-center gap-8 px-8">
              <GradientIconWithShadow icon={Alert} iconVariant="warning" bgVariant="warning" />

              <div className="flex w-full flex-col items-center gap-4">
                <div className="flex w-full flex-col items-center gap-2 text-center">
                  <span className="text-grayscale-black text-xl font-semibold leading-tight">
                    {t(TITLE_KEYS[operation])}
                  </span>
                  <span className="text-gray-80 text-size-sm font-light">
                    {t(useGenericBody ? GENERIC_BODY_KEY : BODY_KEYS[operation])}
                  </span>
                </div>

                <Button
                  variant="secondaryTertiary"
                  size="md"
                  onClick={handleRetry}
                  data-testid={testingConstants.apiKeyActionErrorRetryButton}
                >
                  {t('workspaces_settings.api_keys.errors.try_again')}
                </Button>
              </div>
            </div>
          </div>
        </Popup.Container>
      </Popup>
    );
  }
);

export default ApiKeyActionErrorModal;
